import theme from '@ifca-root/react-component/src/assets/theme'
import CardExpansion from '@ifca-root/react-component/src/components/CardList/CardExpansion'
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import { AccountFooter } from '@ifca-root/react-component/src/components/Footer/AccountFooter'
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Divider,
  Grid,
  Typography,
  Tooltip,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import React, { Fragment, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import {
  useGetAssetDepreciationPeriodsLazyQuery,
  useGenerateDepreciationEntriesMutation,
  AcctPermission,
} from 'generated/graphql'
import { amtStr } from 'helpers/StringNumberFunction/NumFormatters'
import { advancedFilterList } from 'components/Filter/AdvancedFilter'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { usePermissionChecker } from 'helpers/Hooks/usePermissionChecker'

export const DepreciationGenerateSummary = () => {
  let history = useHistory()
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const selectedAssets = JSON.parse(
    localStorage.getItem('selectedDepreciationGenerate') || '[]'
  )

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const [assetsWithPeriods, setAssetsWithPeriods] = useState<any[]>([])
  const [selectedPeriods, setSelectedPeriods] = useState<
    Map<string, Set<string>>
  >(new Map())
  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  /* -------------------------------------------- */
  /*                     QUERY                    */
  /* -------------------------------------------- */

  const [
    loadAssetDepreciationPeriods,
    { loading: getPeriodsLoading },
  ] = useGetAssetDepreciationPeriodsLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: ({ getAssetDepreciationPeriods }) => {
      if (getAssetDepreciationPeriods?.Data?.length > 0) {
        setAssetsWithPeriods(getAssetDepreciationPeriods.Data)
      }
    },
  })

  const [
    generateDepreciation,
    { loading: generateLoading },
  ] = useGenerateDepreciationEntriesMutation({
    onError: ({ message }) => {
      setErrMessage(message?.substring(15))
      setErrDialog(true)
      setShowConfirmDialog(false)
    },
    onCompleted: ({ generateDepreciationEntries }) => {
      if (generateDepreciationEntries?.success) {
        setShowConfirmDialog(false)
        localStorage.removeItem('selectedDepreciationGenerate')
        history.push(
          `/fixed-assets/${CompanyID}/depreciation/generate-depreciation`
        )
      }
    },
  })

  /* -------------------------------------------- */
  /*                   USEEFFECT                  */
  /* -------------------------------------------- */

  useEffect(() => {
    if (selectedAssets.length > 0) {
      const assetIDs = selectedAssets.map((asset: any) => asset.fa_asset_id)
      loadAssetDepreciationPeriods({
        variables: {
          CompanyID: CompanyID,
          AssetIDs: assetIDs,
        },
      })
    }
  }, [])

  /* -------------------------------------------- */
  /*                   FUNCTION                   */
  /* -------------------------------------------- */

  const getLatestAvailablePeriod = (periods: any[]) => {
    const availablePeriods = periods
      .filter(
        (p: any) =>
          !p.ledger_id &&
          (p.period_type === 'past' || p.period_type === 'current')
      )
      .sort((b: any, a: any) => {
        if (a.year !== b.year) return a.year - b.year
        return a.month - b.month
      })

    if (availablePeriods.length > 0) {
      const latest = availablePeriods[availablePeriods.length - 1]
      return `${String(latest.month).padStart(2, '0')}/${latest.year}`
    }
    return null
  }

  const handleSelectAllAssets = (e: any) => {
    const newSelectedPeriods = new Map(selectedPeriods)

    if (e.target.checked) {
      assetsWithPeriods.forEach(asset => {
        const periodSet = new Set(
          asset.periods
            .filter(
              (p: any) =>
                !p.is_posted && !p.ledger_id && p.period_type !== 'future'
            )
            .map((p: any) => `${p.month}/${p.year}`)
        )
        if (periodSet.size > 0) {
          newSelectedPeriods.set(asset.fa_asset_id, periodSet)
        }
      })
    } else {
      newSelectedPeriods.clear()
    }

    setSelectedPeriods(newSelectedPeriods)
  }

  const handleSelectAllPeriods = (assetId: string, periods: any[]) => (
    e: any
  ) => {
    const newSelectedPeriods = new Map(selectedPeriods)

    if (e.target.checked) {
      const periodSet = new Set(
        periods
          .filter(
            p => !p.is_posted && !p.ledger_id && p.period_type !== 'future'
          )
          .map(p => `${p.month}/${p.year}`)
      )
      if (periodSet.size > 0) {
        newSelectedPeriods.set(assetId, periodSet)
      }
    } else {
      newSelectedPeriods.delete(assetId)
    }

    setSelectedPeriods(newSelectedPeriods)
  }

  const handlePeriodCheckboxChange = (assetId: string, period: string) => (
    e: any
  ) => {
    const newSelectedPeriods = new Map(selectedPeriods)
    const assetPeriods = newSelectedPeriods.get(assetId) || new Set()
    const asset = assetsWithPeriods.find(a => a.fa_asset_id === assetId)

    if (!asset) return

    const availablePeriods = asset.periods
      .filter(
        (p: any) => !p.is_posted && !p.ledger_id && p.period_type !== 'future'
      )
      .sort((a: any, b: any) => {
        if (a.year !== b.year) return a.year - b.year
        return a.month - b.month
      })

    const [selectedMonth, selectedYear] = period.split('/').map(Number)
    const selectedIndex = availablePeriods.findIndex(
      (p: any) => p.month === selectedMonth && p.year === selectedYear
    )

    if (selectedIndex === -1) return

    if (e.target.checked) {
      for (let i = 0; i <= selectedIndex; i++) {
        const p = availablePeriods[i]
        const periodKey = `${p.month}/${p.year}`
        assetPeriods.add(periodKey)
      }
    } else {
      for (let i = selectedIndex; i < availablePeriods.length; i++) {
        const p = availablePeriods[i]
        const periodKey = `${p.month}/${p.year}`
        assetPeriods.delete(periodKey)
      }
    }

    if (assetPeriods.size > 0) {
      newSelectedPeriods.set(assetId, assetPeriods)
    } else {
      newSelectedPeriods.delete(assetId)
    }

    setSelectedPeriods(newSelectedPeriods)
  }

  const getTotalSelectedCount = () => {
    let total = 0
    selectedPeriods.forEach(periods => {
      total += periods.size
    })
    return total
  }

  const getTotalSelectedAssets = () => {
    return selectedPeriods.size
  }

  const getTotalDepreciationAmount = () => {
    let total = 0
    selectedPeriods.forEach((periodsSet, assetId) => {
      const asset = assetsWithPeriods.find(a => a.fa_asset_id === assetId)
      if (asset) {
        periodsSet.forEach(periodKey => {
          const [month, year] = periodKey.split('/').map(Number)
          const period = asset.periods.find(
            (p: any) => p.month === month && p.year === year
          )
          if (period) {
            total += period.depreciation_amount || 0
          }
        })
      }
    })
    return total
  }

  const isAssetFullySelected = (assetId: string, periods: any[]) => {
    const availablePeriods = periods.filter(
      p => !p.is_posted && !p.ledger_id && p.period_type !== 'future'
    )
    const selected = selectedPeriods.get(assetId)
    return (
      selected &&
      selected.size === availablePeriods.length &&
      availablePeriods.length > 0
    )
  }

  const areAllAssetsFullySelected = () => {
    if (assetsWithPeriods.length === 0) return false

    for (const asset of assetsWithPeriods) {
      const availablePeriods = asset.periods.filter(
        (p: any) => !p.is_posted && !p.ledger_id && p.period_type !== 'future'
      )
      if (
        availablePeriods.length > 0 &&
        !isAssetFullySelected(asset.fa_asset_id, asset.periods)
      ) {
        return false
      }
    }

    return true
  }
  const isAssetPartiallySelected = (assetId: string, periods: any[]) => {
    const availablePeriods = periods.filter(
      p => !p.is_posted && !p.ledger_id && p.period_type !== 'future'
    )
    const selected = selectedPeriods.get(assetId)

    if (!selected || selected.size === 0) return false
    if (availablePeriods.length === 0) return false

    return selected.size > 0 && selected.size < availablePeriods.length
  }
  const onGenerateClick = () => {
    const selectedCount = getTotalSelectedCount()

    if (selectedCount === 0) {
      alert('Please select at least one period to generate depreciation.')
      return
    }

    setShowConfirmDialog(true)
  }

  const onConfirmGenerate = () => {
    const depreciationData = Array.from(selectedPeriods.entries()).map(
      ([assetId, periodsSet]) => {
        const asset = assetsWithPeriods.find(a => a.fa_asset_id === assetId)
        const selectedPeriodsArray = Array.from(periodsSet)
          .map(periodKey => {
            const [month, year] = periodKey.split('/').map(Number)
            const periodData = asset?.periods.find(
              (p: any) => p.month === month && p.year === year
            )
            return periodData
              ? {
                  month: month,
                  year: year,
                  depreciation_amount: periodData.depreciation_amount,
                  accumulated_depreciation: periodData.accumulated_depreciation,
                  net_book_value: periodData.net_book_value,
                  formula_expression: periodData.formula_expression,
                  fa_depreciation_method_id:
                    periodData.fa_depreciation_method_id,
                  fa_depreciation_formula_id:
                    periodData.fa_depreciation_formula_id,
                }
              : null
          })
          .filter(p => p !== null)
          .sort((a: any, b: any) => {
            if (a.year !== b.year) return a.year - b.year
            return a.month - b.month
          })

        return {
          asset_id: assetId,
          periods: selectedPeriodsArray,
        }
      }
    )

    generateDepreciation({
      variables: {
        CompanyID,
        DepreciationData: depreciationData,
      },
    })
  }

  const { handlePermDisabled } = usePermissionChecker()

  return (
    <>
      {generateLoading && <Loading />}
      {getPeriodsLoading && <Loading />}
      <MainHeader
        mainBtn="back"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/depreciation/generate-depreciation`
          )
          localStorage.removeItem('selectedDepreciationGenerate')
        }}
        smTitle="Fixed Assets"
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Generate Depreciation' },
          { name: 'Summary', current: true },
        ]}
        rightRouteSegments={[{ name: 'Summary', current: true }]}
      />

      <ContentWrapper
        footer
        overflow
        style={{ marginTop: isDesktop ? '70px' : '-7px' }}
      >
        <span className="mdDesc flex-space" style={{ paddingLeft: '7px' }}>
          <Checkbox
            onChange={handleSelectAllAssets}
            color="primary"
            value={''}
            checked={areAllAssetsFullySelected()}
            style={{ marginLeft: '-5px', marginTop: '-5px' }}
          />
          <span style={{ marginLeft: '9px' }}>Select All</span>
        </span>

        <List className="core-list">
          {getPeriodsLoading ? (
            <EmptyList title="Loading..." subtitle="Please wait" />
          ) : assetsWithPeriods === undefined ||
            assetsWithPeriods?.length === 0 ? (
            <EmptyList
              title="No Record found"
              subtitle="No depreciation periods available."
            />
          ) : (
            <>
              {advancedFilterList(assetsWithPeriods).map((asset, index) => {
                console.log('asset: ', asset)

                const availablePeriods = asset.periods.filter(
                  (p: any) =>
                    !p.is_posted && !p.ledger_id && p.period_type !== 'future'
                )
                const latestPeriod = getLatestAvailablePeriod(asset.periods)

                return (
                  <CardExpansion
                    key={asset.fa_asset_id || index}
                    defaultExpanded={index === 0}
                    summary={
                      <ListItem style={{ padding: '8px 16px', width: '100%' }}>
                        <FormControl
                          component="fieldset"
                          style={{ width: '16px', flexShrink: 0 }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                onChange={handleSelectAllPeriods(
                                  asset.fa_asset_id,
                                  asset.periods
                                )}
                                onClick={e => e.stopPropagation()}
                                checked={isAssetFullySelected(
                                  asset.fa_asset_id,
                                  asset.periods
                                )}
                                indeterminate={isAssetPartiallySelected(
                                  asset.fa_asset_id,
                                  asset.periods
                                )}
                                color="primary"
                                style={{
                                  marginLeft: '10px',
                                  marginRight: '-5px',
                                }}
                                disabled={availablePeriods.length === 0}
                              />
                            }
                            label=""
                            name="check"
                          />
                        </FormControl>

                        <ListItemText
                          style={{
                            marginLeft: '25px',
                            flex: 1,
                            minWidth: 0,
                          }}
                          primary={
                            <div style={{ width: '100%' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  gap: '8px',
                                  width: '100%',
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    className="xxTitle"
                                    variant="body2"
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '12px',
                                    }}
                                  >
                                    {asset.DocNo || '-'}
                                  </Typography>
                                  <Typography
                                    className="desc"
                                    style={{
                                      fontSize: '12px',
                                      marginBottom: '2px',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {asset.asset_card_no || '-'}
                                  </Typography>
                                </div>
                                {latestPeriod && (
                                  <Tooltip
                                    disableFocusListener
                                    title={
                                      <Fragment>
                                        <div
                                          style={{
                                            fontSize: '12px',
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          Next Depreciation Period
                                        </div>
                                        <div
                                          style={{
                                            fontSize: '10px',
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          {latestPeriod}
                                        </div>
                                      </Fragment>
                                    }
                                    arrow
                                    enterTouchDelay={0}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: '#fdb15bff',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        flexShrink: 0,
                                        marginLeft: '8px',
                                        cursor: 'default',
                                      }}
                                    >
                                      <Typography
                                        style={{
                                          fontSize: '10px',
                                          color: '#ffffffff',
                                          fontWeight: 'bold',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {latestPeriod}
                                      </Typography>
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          }
                        />
                      </ListItem>
                    }
                  >
                    <div style={{ padding: '0 16px 16px 16px', width: '100%' }}>
                      <Grid
                        container
                        spacing={2}
                        style={{ marginBottom: '8px' }}
                      >
                        <Grid item xs={6}>
                          <div>
                            <Typography
                              className="desc"
                              style={{ fontSize: '10px', color: '#666' }}
                            >
                              Current Original Base Amount
                            </Typography>
                            <Typography
                              className="xxTitle"
                              style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                marginTop: '2px',
                              }}
                            >
                              {asset.base_original_amount
                                ? amtStr(asset.base_original_amount)
                                : '-'}
                            </Typography>
                          </div>
                        </Grid>

                        <Grid item xs={6}>
                          <div>
                            <Typography
                              className="desc"
                              style={{ fontSize: '10px', color: '#666' }}
                            >
                              Current Residual Value
                            </Typography>
                            <Typography
                              className="xxTitle"
                              style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                marginTop: '2px',
                              }}
                            >
                              {asset.residual_value
                                ? amtStr(asset.residual_value)
                                : '-'}
                            </Typography>
                          </div>
                        </Grid>

                        <Grid item xs={6}>
                          <div>
                            <Typography
                              className="desc"
                              style={{ fontSize: '10px', color: '#666' }}
                            >
                              Current Useful Life
                            </Typography>
                            <Typography
                              className="xxTitle"
                              style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                marginTop: '2px',
                              }}
                            >
                              {asset.useful_life
                                ? `${asset.useful_life} months`
                                : '-'}
                            </Typography>
                          </div>
                        </Grid>

                        <Grid item xs={6}>
                          <div>
                            <Typography
                              className="desc"
                              style={{ fontSize: '10px', color: '#666' }}
                            >
                              Current Utilized Life
                            </Typography>
                            <Typography
                              className="xxTitle"
                              style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                marginTop: '2px',
                              }}
                            >
                              {asset.utilized_useful_life !== null &&
                              asset.utilized_useful_life !== undefined
                                ? `${asset.utilized_useful_life} months`
                                : '0 months'}
                            </Typography>
                          </div>
                        </Grid>
                      </Grid>

                      <Divider style={{ margin: '16px 0' }} />

                      <Typography
                        className="desc"
                        style={{ marginBottom: '12px', fontWeight: 'bold' }}
                      >
                        Select Depreciation Periods:
                      </Typography>

                      {asset.periods.map((period: any, pIndex: number) => {
                        const periodKey = `${period.month}/${period.year}`
                        const isSelected =
                          selectedPeriods
                            .get(asset.fa_asset_id)
                            ?.has(periodKey) || false
                        const isGenerated = period.ledger_id !== null
                        const isFuture = period.period_type === 'future'

                        return (
                          <div
                            key={pIndex}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom:
                                pIndex < asset.periods.length - 1
                                  ? '1px solid #f0f0f0'
                                  : 'none',
                              width: '100%',
                              opacity: isFuture ? 0.5 : 1,
                            }}
                          >
                            {isGenerated ? (
                              <CheckCircleIcon
                                style={{
                                  color: '#4CAF50',
                                  marginRight: '8px',
                                  fontSize: '24px',
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <Checkbox
                                checked={isSelected}
                                onChange={handlePeriodCheckboxChange(
                                  asset.fa_asset_id,
                                  periodKey
                                )}
                                color="primary"
                                disabled={period.is_posted || isFuture}
                                style={{ marginRight: '8px', flexShrink: 0 }}
                              />
                            )}
                            <div
                              style={{ flex: 1, minWidth: 0, width: '100%' }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  gap: '8px',
                                  width: '100%',
                                }}
                              >
                                <Typography
                                  className="xxTitle"
                                  style={{
                                    fontSize: '13px',
                                    color: isGenerated
                                      ? '#4CAF50'
                                      : period.is_posted || isFuture
                                      ? '#999'
                                      : '#000',
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {period.period}
                                  {isGenerated && (
                                    <span
                                      style={{
                                        marginLeft: '8px',
                                        fontSize: '12px',
                                        color: '#4CAF50',
                                      }}
                                    >
                                      (Generated)
                                    </span>
                                  )}
                                  {isFuture && (
                                    <span
                                      style={{
                                        marginLeft: '8px',
                                        fontSize: '12px',
                                        color: '#ff9800',
                                      }}
                                    >
                                      (Future)
                                    </span>
                                  )}
                                </Typography>
                                <Typography
                                  className="desc"
                                  style={{ flexShrink: 0 }}
                                >
                                  {amtStr(period.depreciation_amount || 0)}
                                </Typography>
                              </div>
                              <Typography
                                style={{
                                  fontSize: '12px',
                                  color: '#666',
                                  marginTop: '4px',
                                  wordBreak: 'break-word',
                                }}
                              >
                                Acc. Dep:{' '}
                                {amtStr(period.accumulated_depreciation || 0)} |
                                Net Book Value:{' '}
                                {amtStr(period.net_book_value || 0)}
                              </Typography>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </CardExpansion>
                )
              })}
            </>
          )}
        </List>
      </ContentWrapper>

      {/* Confirmation Dialog */}
      <CommonDialog
        fullWidth
        open={showConfirmDialog}
        onClose={() => !generateLoading && setShowConfirmDialog(false)}
        sections={{
          header: {
            title: 'Generate Depreciation',
            rightText: 'Confirm',
            infoLine: `${getTotalSelectedAssets()} Assets • ${getTotalSelectedCount()} Periods • ${amtStr(
              getTotalDepreciationAmount()
            )}`,
          },
          body: () => (
            <div className="content-wrap full">
              <span
                style={{
                  fontSize: '12px',
                  lineHeight: '14px',
                  fontWeight: 500,
                }}
              >
                {generateLoading
                  ? 'Generating depreciation entries...'
                  : `Are you sure you want to generate ${getTotalSelectedCount()} depreciation ${
                      getTotalSelectedCount() === 1 ? 'entry' : 'entries'
                    } for ${getTotalSelectedAssets()} ${
                      getTotalSelectedAssets() === 1 ? 'asset' : 'assets'
                    }?`}
              </span>
            </div>
          ),
          footer: {
            actions: [
              {
                displayText: 'Cancel',
                props: {
                  onClick: () => setShowConfirmDialog(false),
                  variant: 'contained',
                  color: 'default',
                  disabled: generateLoading,
                },
              },
              {
                displayText: generateLoading ? 'Generating...' : 'Confirm',
                props: {
                  onClick: onConfirmGenerate,
                  variant: 'contained',
                  color: 'primary',
                  disabled: generateLoading,
                },
              },
            ],
          },
        }}
      />

      <AccountFooter
        options={[
          {
            name: (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Generate
                {getTotalSelectedCount() > 0 ? (
                  <span
                    style={{
                      marginLeft: '8px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      color: '#000',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                  >
                    {getTotalSelectedCount()}
                  </span>
                ) : null}
              </span>
            ),
            onClick: onGenerateClick,
            color: 'primary',
            props: {
              disabled:
                getTotalSelectedCount() === 0 ||
                handlePermDisabled({
                  companyID: CompanyID,
                  permEnum:
                    AcctPermission.FixedAssetsAssetsRegistrationApproveReject,
                }),
            },
          },
        ]}
      />
    </>
  )
}
